import React, { useState, useEffect } from 'react';
import auth from '../AuthService';
import './BotStatus.css';

function SetupBot() {
  const [downloadError, setDownloadError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [hasAppPassword, setHasAppPassword] = useState(null);
  const [appPassword, setAppPassword] = useState(null);
  const [appPasswordError, setAppPasswordError] = useState(null);
  const [appPasswordLoading, setAppPasswordLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  async function fetchProfile() {
    try {
      const res = await auth.fetch('/accounts/profile');
      setHasAppPassword(res.hasAppPassword);
    } catch (err) {
      setHasAppPassword(null);
    }
  }

  const handleDownload = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch('/downloads/satellite-bot', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const fname = cd.split('filename=')[1]?.replace(/"/g, '') || 'MLGC-Satellite-Bot';
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleGenerateAppPassword = async () => {
    setAppPasswordError(null);
    setAppPasswordLoading(true);
    try {
      const res = await auth.fetch('/accounts/app-password', {
        method: 'POST',
      });
      setAppPassword(res.password);
      setHasAppPassword(true);
    } catch (err) {
      setAppPasswordError(err.message || 'Failed to generate bot password');
    } finally {
      setAppPasswordLoading(false);
    }
  };

  const handleRevokeAppPassword = async () => {
    if (!window.confirm('Revoke your bot password? Any satellite bots using it will stop working.')) return;
    setAppPasswordError(null);
    setAppPasswordLoading(true);
    try {
      await auth.fetch('/accounts/app-password', {
        method: 'DELETE',
      });
      setAppPassword(null);
      setHasAppPassword(false);
    } catch (err) {
      setAppPasswordError(err.message || 'Failed to revoke bot password');
    } finally {
      setAppPasswordLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (appPassword && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(appPassword);
    } else if (appPassword) {
      const textarea = document.createElement('textarea');
      textarea.value = appPassword;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); } catch (_) { /* no-op */ }
      document.body.removeChild(textarea);
    }
  };

  return (
    <div className="bot-status-page">
      <h3>Setup Bot</h3>
      <p>Download and install the satellite bot on your machine to start sniping bookings.</p>
      {downloadError && <div className="bot-error">Error: {downloadError}</div>}
      <button
        className="bot-download-btn btn btn-primary"
        onClick={handleDownload}
        disabled={downloading}
      >
        {downloading ? 'Downloading...' : 'Download Satellite Bot'}
      </button>

      <hr style={{ margin: '30px 0' }} />

      <h4>Bot Password</h4>
      {appPasswordError && (
        <div className="bot-error" style={{ marginTop: '10px' }}>Error: {appPasswordError}</div>
      )}
      {appPassword && (
        <div className="alert alert-success" role="alert" style={{ marginTop: '15px' }}>
          <p>
            <strong>Your bot password is:</strong>
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '10px 0' }}>
            <input
              type="text"
              className="form-control"
              value={appPassword}
              readOnly
              style={{ fontFamily: 'monospace', fontSize: '1.1rem', flex: 1 }}
            />
            <button className="btn btn-outline-secondary" onClick={handleCopyPassword}>
              Copy
            </button>
          </div>
          <p className="text-warning" style={{ marginTop: '10px' }}>
            <strong>Important:</strong> This password will not be shown again. Paste it into the satellite bot app along with your frontend username.
          </p>
        </div>
      )}
      <div style={{ marginTop: '15px' }}>
        {hasAppPassword && !appPassword ? (
          <div>
            <p className="text-muted">Bot password is active.</p>
            <button
              className="btn btn-outline-danger"
              onClick={handleRevokeAppPassword}
              disabled={appPasswordLoading}
            >
              {appPasswordLoading ? 'Revoking...' : 'Revoke Bot Password'}
            </button>
          </div>
        ) : (
          <button
            className="btn btn-outline-primary"
            onClick={handleGenerateAppPassword}
            disabled={appPasswordLoading}
          >
            {appPasswordLoading ? 'Generating...' : 'Generate Bot Password'}
          </button>
        )}
      </div>
    </div>
  );
}

export default SetupBot;
